import type { ReactNode } from "react";

const B = ({ children }: { children: ReactNode }) => (
  <strong className="font-medium text-[var(--color-fg)]">{children}</strong>
);

const cards: { title: string; badge: string; description: ReactNode }[] = [
  {
    title: "Acquire Data",
    badge: "ACTIVE",
    description: <>Partner with and acquire regulated firms. Ingest <B>real case data</B>, workflows, and compliance requirements.</>,
  },
  {
    title: "Build Benchmarks",
    badge: "ACTIVE",
    description: <>Establish <B>accuracy baselines</B> per workflow. Agents are measured against real professional standards.</>,
  },
  {
    title: "Agent Evolution",
    badge: "ACTIVE",
    description: <>Harness engineering: agents <B>self-evaluate</B> against benchmarks and improve <B>autonomously</B>. The flywheel compounds.</>,
  },
];

export function V2EngineSlide({ isActive: _ }: { isActive: boolean }) {
  return (
    <div className="flex flex-col h-full">
      <h2 className="text-[32px] font-light tracking-tight mb-3 font-[family-name:var(--font-heading)]">
        One engine. Three stages.<br />Compounding intelligence.
      </h2>
      <p className="text-[14px] text-[var(--color-fg-secondary)] leading-relaxed font-light mb-5">
        Real operational data in. Domain-specific benchmarks out. Agents improve themselves against those baselines.<br /><strong className="font-medium text-[var(--color-fg)]">More cases, more accuracy. More verticals, more skills.</strong>
      </p>

      <div className="flex gap-4">
        {cards.map((card) => (
          <div
            key={card.title}
            className="flex-1 flex flex-col border border-[var(--color-border)] rounded-sm overflow-hidden"
          >
            <div className="h-[3px] bg-emerald-500" />
            <div className="px-5 py-4 flex flex-col">
              <div className="flex items-center gap-3 mb-2">
                <h3 className="text-[14px] font-medium font-[family-name:var(--font-heading)]">
                  {card.title}
                </h3>
                <span className="text-[8px] font-[family-name:var(--font-mono)] tracking-widest uppercase px-2 py-0.5 rounded-sm bg-emerald-500/10 text-emerald-500">
                  {card.badge}
                </span>
              </div>
              <p className="text-[12px] leading-relaxed text-[var(--color-fg-secondary)] font-light">
                {card.description}
              </p>
            </div>
          </div>
        ))}
      </div>

      {/* Deploy / Manage / Govern — parallel perspective */}
      <div className="flex gap-4 mt-3">
        {[
          { label: "Deploy", accent: "#3b82f6", text: "AI agents into live regulated workflows with human QA" },
          { label: "Manage", accent: "#8b5cf6", text: "Agent identity, access, and cost tracking per task" },
          { label: "Govern", accent: "#f59e0b", text: "Full audit trails and compliance gates" },
        ].map((item) => (
          <div key={item.label} className="flex-1 border border-[var(--color-border)] rounded-sm overflow-hidden">
            <div className="h-[3px]" style={{ background: item.accent }} />
            <div className="px-4 py-3">
              <h3 className="text-[12px] font-medium mb-1">{item.label}</h3>
              <p className="text-[10px] text-[var(--color-fg-secondary)] leading-snug">{item.text}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="text-left mt-auto mb-8 pb-4 border-b border-[var(--color-border)]">
        <p className="text-[12px] font-medium text-[var(--color-fg)]">
          The data makes the agents better. The agents make the data more valuable.
        </p>
      </div>
    </div>
  );
}
